import {
  AUTOMATION_RUN_STATUS_LABELS,
  type AutomationDecisionDTO,
  type AutomationRunDTO,
  type AutomationStatusDTO,
  type RunAutomationResponse,
  type SequenceSummaryDTO,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Play, Plus, Workflow } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { RelativeTime } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, CardBody, EmptyState, Skeleton, Tabs, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, errorMessage } from '../../lib/api';
import { formatNumber, pluralize } from '../../lib/format';
import { DecisionList } from './DecisionList';

type Tab = 'sequences' | 'history' | 'runs';

const RUN_COLORS: Record<AutomationRunDTO['status'], string> = { running: 'sky', completed: 'green', failed: 'red', skipped: 'amber' };

export function AutomationPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('sequences');
  const manage = can('automation:manage');

  const { data: status } = useQuery({
    queryKey: ['automation', 'status'],
    queryFn: ({ signal }) => api.get<AutomationStatusDTO>('/api/automation/status', { signal }),
    refetchInterval: (query) => (query.state.data?.runQueued ? 3_000 : 60_000),
  });
  const { data: sequences, isLoading } = useQuery({
    queryKey: ['automation', 'sequences'],
    queryFn: ({ signal }) => api.get<{ items: SequenceSummaryDTO[] }>('/api/sequences', { signal }),
  });
  const { data: decisions } = useQuery({
    queryKey: ['automation', 'decisions'],
    queryFn: ({ signal }) => api.get<{ items: AutomationDecisionDTO[] }>('/api/automation/decisions?pageSize=100', { signal }),
    enabled: tab === 'history',
  });
  const { data: runs } = useQuery({
    queryKey: ['automation', 'runs'],
    queryFn: ({ signal }) => api.get<{ items: AutomationRunDTO[] }>('/api/automation/runs?pageSize=50', { signal }),
    enabled: tab === 'runs',
  });

  const runNow = useMutation({
    mutationFn: () => api.post<RunAutomationResponse>('/api/automation/run'),
    onSuccess: async (result) => {
      toast.success(result.queued ? 'Automation run started' : 'A run is already queued');
      await queryClient.invalidateQueries({ queryKey: ['automation'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const lastRun = status?.lastRun;
  return (
    <Page width="wide">
      <PageHeader
        title="Automation"
        description="Check-in and follow-up sequences that write to pizzerias for you — every decision is explained."
        actions={
          manage ? (
            <div className="flex gap-2">
              <Button icon={Play} loading={runNow.isPending || status?.runQueued} onClick={() => runNow.mutate()}>
                Run now
              </Button>
              <Link to="/automation/sequences/new">
                <Button variant="primary" icon={Plus}>
                  New sequence
                </Button>
              </Link>
            </div>
          ) : undefined
        }
      />

      {status && (
        <Card className={status.blockers.length > 0 ? 'mb-4 border-amber-300 bg-amber-50' : 'mb-4'}>
          <CardBody className="flex flex-wrap items-start gap-4 text-[13px]">
            <div className="min-w-0 flex-1">
              {status.blockers.length > 0 ? (
                <>
                  <p className="flex items-center gap-1 font-medium text-amber-900">
                    <AlertTriangle className="size-4" /> Automation is not sending
                  </p>
                  <ul className="mt-1 list-disc pl-5 text-amber-900">
                    {status.blockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="font-medium text-emerald-800">
                  Automation is on — runs every {status.runIntervalMinutes} minutes, up to {formatNumber(status.maxEnrollmentsPerRun)} new enrollments per run.
                </p>
              )}
            </div>
            <div className="text-right text-slate-600">
              {lastRun ? (
                <>
                  <p>
                    Last run <RelativeTime value={lastRun.startedAt} />{' '}
                    <Badge color={RUN_COLORS[lastRun.status]} size="sm">
                      {AUTOMATION_RUN_STATUS_LABELS[lastRun.status]}
                    </Badge>
                  </p>
                  <p className="text-xs">
                    {lastRun.enrolledCount} enrolled · {lastRun.queuedCount} emails queued · {lastRun.stoppedCount} stopped
                  </p>
                </>
              ) : (
                <p>Never run yet</p>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <Tabs
          tabs={[
            { key: 'sequences', label: 'Sequences', count: sequences?.items.length },
            { key: 'history', label: 'Decisions' },
            { key: 'runs', label: 'Runs' },
          ]}
          value={tab}
          onChange={setTab}
        />
        {tab === 'sequences' &&
          (isLoading ? (
            <Skeleton className="m-4 h-32" />
          ) : (sequences?.items.length ?? 0) === 0 ? (
            <EmptyState
              icon={Workflow}
              title="No sequences yet"
              description="A sequence is a short series of emails — e.g. a 30-day check-in with a pizzeria that went quiet."
              action={
                manage ? (
                  <Link to="/automation/sequences/new">
                    <Button variant="primary">Create a sequence</Button>
                  </Link>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {sequences!.items.map((sequence) => (
                <li key={sequence.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-[13px]">
                  <Link to={`/automation/sequences/${sequence.id}`} className="font-medium text-slate-900 hover:text-ember-700">
                    {sequence.name}
                  </Link>
                  <Badge color={sequence.isActive ? 'green' : 'gray'} size="sm">
                    {sequence.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  {sequence.autoEnroll && <Badge size="sm">Auto-enroll</Badge>}
                  {sequence.requireApproval && <Badge color="amber" size="sm">Approval</Badge>}
                  <span className="ml-auto text-slate-600">
                    {pluralize(sequence.stepCount, 'step')} · {sequence.activeEnrollments} active · {sequence.pausedEnrollments} paused ·{' '}
                    {pluralize(sequence.emailsQueued30d, 'email')} in 30 days
                  </span>
                </li>
              ))}
            </ul>
          ))}
        {tab === 'history' && <DecisionList items={decisions?.items ?? []} />}
        {tab === 'runs' && (
          <ul className="divide-y divide-slate-100">
            {(runs?.items ?? []).map((run) => (
              <li key={run.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-[13px]">
                <Badge color={RUN_COLORS[run.status]} size="sm">
                  {AUTOMATION_RUN_STATUS_LABELS[run.status]}
                </Badge>
                <span className="text-slate-500">{run.trigger === 'manual' ? 'Run now' : 'Scheduled'}</span>
                <RelativeTime value={run.startedAt} />
                <span className="text-slate-600">
                  {run.enrolledCount} enrolled · {run.queuedCount} queued · {run.skippedCount} skipped · {run.stoppedCount} stopped
                </span>
                {run.error && <span className="basis-full text-xs text-amber-800">{run.error}</span>}
              </li>
            ))}
            {runs && runs.items.length === 0 && <li className="px-4 py-6 text-center text-[13px] text-slate-500">No runs yet.</li>}
          </ul>
        )}
      </Card>
    </Page>
  );
}
